import { flatExtents } from "building-generation/elements/openings/openingUtils";
import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { WINDOW_EXTERIOR_FRAME_DEPTH } from "doc/elements/window/constants";
import { isHatchMaterialPaletteSlot } from "doc/utils/wallMaterialTools";
import { DIST_EPSILON, ANGLE_EPSILON } from "geom/core";
import { intersectPolyLine3Plane } from "geom/intersect";
import { line2Normal } from "geom/line2";
import { polyLine2Thicken } from "geom/offset";
import { planeXYAtZ, polyLine2OnPlane, planeNormal, planeProject, planeReProjectPolyLine2 } from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { polyLine2Simplify, polyLine2Vertex, polyLine2VertexCount } from "geom/polyLine2";
import { polyLine2MinMaxIndices } from "geom/polyLine2MinMax";
import { type PolyLine2, polyLine2New } from "geom/polyLine2Types";
import { polyLine3New, polyLine3Vertex } from "geom/polyLine3";
import { clipPolygonsAgainstPlane } from "geom/sutherlandHodgman";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2Scale } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3AngleBetween } from "geom/vec3";

export function openingSidePlanCutPlane(side: OpeningSide, openingPlane: Plane): Plane | null {
  const opening2 = side.opening2;
  const opening3 = side.opening3 || polyLine2OnPlane(openingPlane, opening2);

  // min max on X axis
  const { min, max } = polyLine2MinMaxIndices(opening2, 1, 0);

  // gather the min/max z heights of the opening min and maxes;
  let minZIntersection = Infinity;
  for (const i of min) {
    const z = polyLine3Vertex(opening3, i)[2];
    if (z < minZIntersection) {
      minZIntersection = z;
    }
  }
  for (const i of max) {
    const z = polyLine3Vertex(opening3, i)[2];
    if (z < minZIntersection) {
      minZIntersection = z;
    }
  }

  return planeXYAtZ(minZIntersection);
}

export function makeProjectedTrimPlanLinework(
  trim: OpeningTrim,
  cutPlane: Plane,
  openingPlane: Plane,
  linework: PolyLine2[],
  tolerance = ANGLE_EPSILON,
) {
  const { depth, elevationProfile } = trim;
  if (!elevationProfile || !depth) {
    return;
  }
  // reproject the rail into the XY plane
  const rail3 = elevationProfile.map((p) => polyLine2OnPlane(openingPlane, p));

  // clip the rail and preserve points that are "below" (in the negative plane normal direction)
  // of the cut plane
  const planeEqn = planeBSPFromPlane(cutPlane);
  const [clipped3] = clipPolygonsAgainstPlane(
    rail3.map((n) => [...n]),
    planeEqn,
    false,
  ).map(polyLine3New);
  if (!clipped3) {
    return;
  }

  const extents = flatExtents(clipped3);
  if (!extents) {
    return;
  }
  let flat2 = polyLine2New(extents);

  flat2 = polyLine2Simplify(flat2, tolerance);
  const x0 = flat2[0];
  const y0 = flat2[1];
  const x1 = flat2[flat2.length - 2];
  const y1 = flat2[flat2.length - 1];

  const [pnx, pny] = planeNormal(openingPlane);
  const [dx, dy] = vec2Scale(pnx, pny, depth);

  linework.push(polyLine2New([x0, y0, x0 + dx, y0 + dy, x1 + dx, y1 + dy, x1, y1, x0, y0]));
}

export function makeCutTrimPlanLinework(
  trim: OpeningTrim,
  cutPlane: Plane,
  openingPlane: Plane,
  linework: PolyLine2[],
  tolerance = DIST_EPSILON,
  angleTolerance = ANGLE_EPSILON,
) {
  if (trim.type !== "SIDE_CASING") {
    return;
  }

  const { elevationProfile, depth } = trim;
  if (!elevationProfile) {
    return;
  }

  const elevation3 = elevationProfile.map((p) => polyLine2OnPlane(openingPlane, p));

  const intersections = elevation3
    .map((e) => intersectPolyLine3Plane(cutPlane, e, tolerance))
    .flat()
    .map((i) => [i.intersection.x, i.intersection.y, i.intersection.z] as Vec3);

  const clean = xSortDedupe(intersections, cutPlane, openingPlane);

  for (let i = 0; i < clean.length; i += 2) {
    if (!clean[i + 1]) {
      break;
    }
    let x1 = clean[i][0];
    let y1 = clean[i][1];
    let x2 = clean[i + 1][0];
    let y2 = clean[i + 1][1];

    // check angle between cut line and plane normal; if greater than ~0, reverse
    const [lnx, lny] = line2Normal(x1, y1, x2, y2);
    const [pnx, pny, pnz] = planeNormal(openingPlane);
    const angle = vec3AngleBetween(lnx, lny, 0, pnx, pny, pnz);
    if (angle > angleTolerance) {
      [x2, y2, x1, y1] = [x1, y1, x2, y2];
    }

    linework.push(polyLine2Thicken(polyLine2New([x1, y1, x2, y2]), depth ?? WINDOW_EXTERIOR_FRAME_DEPTH));
  }
}

export function makeSheetGeometryForOpeningCutTrim(handle: DocEntityHandle, opening: WallOpening) {
  const { front, back, frontPlane, backPlane } = opening;

  return [
    ...makeSheetGeometryForOpeningSideCutTrim(handle, front, frontPlane),
    ...makeSheetGeometryForOpeningSideCutTrim(handle, back, backPlane),
  ];
}

export function makeSheetGeometryForOpeningProjectedTrim(handle: DocEntityHandle, opening: WallOpening) {
  const { front, back, frontPlane, backPlane } = opening;

  return [
    ...makeSheetGeometryForOpeningSideProjectedTrim(handle, front, frontPlane),
    ...makeSheetGeometryForOpeningSideProjectedTrim(handle, back, backPlane),
  ];
}

export function makeSheetGeometryForOpeningSideCutTrim(
  handle: DocEntityHandle,
  side: OpeningSide,
  openingPlane: Plane,
) {
  const result: SheetGeometry[] = [];

  for (const trim of side.trim.filter((t) => t.type === "SIDE_CASING")) {
    const linework: PolyLine2[] = [];

    const cutPlane = openingSidePlanCutPlane(side, openingPlane);
    if (!cutPlane) {
      continue;
    }

    makeCutTrimPlanLinework(trim, cutPlane, openingPlane, linework);

    const hatch = !!trim.materialId && !!isHatchMaterialPaletteSlot[trim.materialId];

    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: linework,
        hatch,
      }),
    );
  }

  return result;
}

export function makeSheetGeometryForOpeningSideProjectedTrim(
  handle: DocEntityHandle,
  side: OpeningSide,
  openingPlane: Plane,
) {
  const result: SheetGeometry[] = [];

  for (const trim of side.trim) {
    const linework: PolyLine2[] = [];

    const cutPlane = openingSidePlanCutPlane(side, openingPlane);
    if (!cutPlane) {
      continue;
    }

    makeProjectedTrimPlanLinework(trim, cutPlane, openingPlane, linework);

    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: linework,
      }),
    );
  }

  return result;
}

function xSortDedupe(ints: Vec3[], origPlane: Plane, testPlane: Plane, tolerance: number = DIST_EPSILON): Vec2[] {
  // project intersection3s onto plane and store with original, then sort by x
  const xOnPlane: [Vec3, Vec2][] = ints
    .map((i) => {
      const [px, py] = planeProject(testPlane, i[0], i[1], i[2]);
      return [i, [px, py]] as [Vec3, Vec2];
    })
    // sort by 2D x value projected in plane
    .sort(([, av2], [, bv2]) => av2[0] - bv2[0]);

  // deduplicate by distance from previous point
  const deduped: [Vec3, Vec2][] = [];
  for (const p of xOnPlane) {
    const [, [x1, y1]] = p;
    if (deduped.length === 0) {
      deduped.push(p);
      continue;
    }
    // get previous point
    const [, [x0, y0]] = deduped[deduped.length - 1];
    if (vec2Dist(x0, y0, x1, y1) > tolerance) {
      deduped.push(p);
    }
  }

  const asPline = polyLine2New(deduped.map(([, v2]) => v2).flat());

  const unprojected = planeReProjectPolyLine2(asPline, testPlane, origPlane);

  const asVec2: Vec2[] = [];
  const numVertexes = polyLine2VertexCount(unprojected);
  for (let i = 0; i < numVertexes; i++) {
    asVec2.push([...polyLine2Vertex(unprojected, i)]);
  }

  return asVec2;
}
